"use client";
import { createCache, extractStyle, StyleProvider } from "@ant-design/cssinjs";
import { useServerInsertedHTML } from "next/navigation";
import React, { useState } from "react";

// Moves the <style> just before it above the first stylesheet <link> in
// <head>. Runs inline during parsing, so it happens before first paint.
const HOIST_SCRIPT =
  "(function(){var s=document.currentScript,t=s&&s.previousElementSibling," +
  "h=document.head,l=h&&h.querySelector('link[rel=\"stylesheet\"]');" +
  "if(t&&l&&t.id==='antd-cssinjs')h.insertBefore(t,l)})()";

/**
 * @ant-design/nextjs-registry's AntdRegistry, plus one fix: antd's
 * server-rendered styles are moved ahead of the app's stylesheets.
 *
 * AntdRegistry emits antd's styles through useServerInsertedHTML, but React
 * hoists every stylesheet <link> to the top of <head>, so the <style> tag
 * ends up after them. Its data-rc-order="prepend" asks for the opposite, and
 * antd's client doesn't re-inject the styles or move the tag. They stay where
 * they are.
 *
 * Order decides every tie: antd's rules use zero-specificity :where(), and so
 * do many of the app's own resets. Before SSR styles worked (a duplicate
 * @ant-design/cssinjs split the registry from antd), antd injected its styles
 * client-side at the start of <head>, and the app's CSS won those ties. With
 * the tag last, antd won them instead. One example: `:where(.css-…) a` beat
 * globals.css's `a { color: inherit }`, and links across the site changed
 * colour.
 *
 * Hoisting the tag restores that cascade: antd first, then the app.
 */
export default function AntdStyleRegistry({ children }) {
  const [cache] = useState(() => createCache());

  useServerInsertedHTML(() => {
    const styleText = extractStyle(cache, { plain: true, once: true });
    if (styleText.includes('.data-ant-cssinjs-cache-path{content:"";}')) {
      return null;
    }
    return (
      <>
        <style
          id="antd-cssinjs"
          data-rc-order="prepend"
          data-rc-priority="-1000"
          dangerouslySetInnerHTML={{ __html: styleText }}
        />
        <script dangerouslySetInnerHTML={{ __html: HOIST_SCRIPT }} />
      </>
    );
  });

  return <StyleProvider cache={cache}>{children}</StyleProvider>;
}
